import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  Phone,
  Plus,
  Search,
  RefreshCw,
  Shield,
  Trash2,
  Edit,
  Infinity,
  AlertCircle,
  CheckCircle,
  X,
  RotateCcw,
  ArrowLeft,
  ChevronDown,
  MessageCircle,
  Signal,
  Gauge,
} from 'lucide-react';
import Header from '../../components/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmDialog, { useConfirmDialog } from '../../components/ui/ConfirmDialog';
import { getQualityRatingColor, getQualityRatingLabel, formatTier } from '../../utils/statusColors';
import {
  apiClient,
  UserWabaAccess,
  GrantWabaAccessDto,
  UpdateWabaAccessDto,
  WabaAccount,
  User,
} from '../../services/api';

export default function WabaAccessManagement() {
  const navigate = useNavigate();
  const { accessToken, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [wabaAccounts, setWabaAccounts] = useState<WabaAccount[]>([]);
  const [selectedWaba, setSelectedWaba] = useState<WabaAccount | null>(null);
  const [accessList, setAccessList] = useState<UserWabaAccess[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [showIncludeInactive, setShowIncludeInactive] = useState(false);

  // Modal states
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState<UserWabaAccess | null>(null);

  // Form states
  const [grantUserId, setGrantUserId] = useState('');
  const [grantDailyLimit, setGrantDailyLimit] = useState('0');
  const [grantNotes, setGrantNotes] = useState('');
  const [editDailyLimit, setEditDailyLimit] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editNotes, setEditNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();
  const [showWabaDropdown, setShowWabaDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check if user has permission before loading
  const hasPermission = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  // Helper function to get quality rating badge
  const getQualityBadge = (rating?: string) => {
    return { color: getQualityRatingColor(rating), label: getQualityRatingLabel(rating) };
  };

  // Helper function to get messaging limit label
  const getMessagingLimitLabel = (tier?: string) => {
    return formatTier(tier);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowWabaDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (hasPermission) {
      loadWabaAccounts();
      loadUsers();
    }
  }, [accessToken, hasPermission]);

  useEffect(() => {
    if (selectedWaba) {
      loadAccessList();
    }
  }, [selectedWaba, showIncludeInactive]);

  const loadWabaAccounts = async () => {
    if (!accessToken) return;

    try {
      const response = await apiClient.getWabaAccounts(accessToken);
      if (response.success && response.data?.wabaAccounts) {
        setWabaAccounts(response.data.wabaAccounts);
        if (response.data.wabaAccounts.length > 0 && !selectedWaba) {
          setSelectedWaba(response.data.wabaAccounts[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar contas WABA:', error);
    }
  };

  const loadUsers = async () => {
    if (!accessToken) return;

    try {
      // Obter tenantId do usuário atual
      const tenantId = currentUser?.tenantId || currentUser?.tenant?.id;

      const response = await apiClient.getUsers({ limit: 100, tenantId }, accessToken);
      if (response.success) {
        // Backend retorna data como UserListResponse que tem { data: User[], meta: {...} }
        const usersData = response.data;
        if (Array.isArray(usersData)) {
          setUsers(usersData);
        } else if (usersData?.data && Array.isArray(usersData.data)) {
          setUsers(usersData.data);
        } else {
          setUsers([]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar usuarios:', error);
    }
  };

  const loadAccessList = async () => {
    if (!accessToken || !selectedWaba) return;

    try {
      setLoading(true);
      const response = await apiClient.getWabaUsers(
        selectedWaba.id,
        showIncludeInactive,
        accessToken
      );
      if (response.success) {
        setAccessList(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar lista de acesso:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!accessToken || !selectedWaba) return;

    setError('');
    setSuccess('');

    if (!grantUserId) {
      setError('Selecione um usuario');
      return;
    }

    try {
      setSaving(true);

      const data: GrantWabaAccessDto = {
        userId: grantUserId,
        wabaAccountId: selectedWaba.id,
        dailyLimit: parseInt(grantDailyLimit) || 0,
        notes: grantNotes || undefined,
      };

      const response = await apiClient.grantWabaAccess(data, accessToken);

      if (response.success) {
        setSuccess('Acesso concedido com sucesso!');
        setShowGrantModal(false);
        setGrantUserId('');
        setGrantDailyLimit('0');
        setGrantNotes('');
        await loadAccessList();
      }
    } catch (error: any) {
      setError(error.message || 'Erro ao conceder acesso');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAccess = async () => {
    if (!accessToken || !selectedAccess) return;

    setError('');
    setSuccess('');

    try {
      setSaving(true);

      const data: UpdateWabaAccessDto = {
        dailyLimit: parseInt(editDailyLimit) || 0,
        isActive: editIsActive,
        notes: editNotes || undefined,
      };

      const response = await apiClient.updateWabaAccess(
        selectedAccess.userId,
        selectedAccess.wabaAccountId,
        data,
        accessToken
      );

      if (response.success) {
        setSuccess('Acesso atualizado com sucesso!');
        setShowEditModal(false);
        setSelectedAccess(null);
        await loadAccessList();
      }
    } catch (error: any) {
      setError(error.message || 'Erro ao atualizar acesso');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeAccess = (access: UserWabaAccess) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Revogar acesso',
      message: `Tem certeza que deseja revogar o acesso de ${access.user?.name}?`,
      variant: 'danger',
      confirmText: 'Revogar',
      onConfirm: async () => {
        try {
          await apiClient.revokeWabaAccess(access.userId, access.wabaAccountId, accessToken);
          setSuccess('Acesso revogado com sucesso!');
          await loadAccessList();
        } catch (error: any) {
          setError(error.message || 'Erro ao revogar acesso');
        }
      },
    });
  };

  const handleResetUsage = (access: UserWabaAccess) => {
    if (!accessToken) return;

    showConfirm({
      title: 'Zerar uso',
      message: `Tem certeza que deseja zerar o uso de ${access.user?.name}?`,
      variant: 'warning',
      confirmText: 'Zerar',
      onConfirm: async () => {
        try {
          await apiClient.resetWabaUsage(access.userId, access.wabaAccountId, accessToken);
          setSuccess('Uso zerado com sucesso!');
          await loadAccessList();
        } catch (error: any) {
          setError(error.message || 'Erro ao zerar uso');
        }
      },
    });
  };

  const openEditModal = (access: UserWabaAccess) => {
    setSelectedAccess(access);
    setEditDailyLimit(access.dailyLimit.toString());
    setEditIsActive(access.isActive);
    setEditNotes(access.notes || '');
    setShowEditModal(true);
  };

  // Check permissions (roles are lowercase from backend)
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-bg-1">
        <Header title="Gestao de Acesso WhatsApp" />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-bg-2 border border-error/30 rounded-lg p-8 text-center">
            <Shield size={48} className="mx-auto text-error mb-4" />
            <h2 className="text-xl font-semibold text-text-1 mb-2">Acesso Negado</h2>
            <p className="text-text-2">
              Voce nao tem permissao para gerenciar acesso WhatsApp.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-1">
      <Header title="Gestao de Acesso WhatsApp" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg flex items-center gap-2 text-error">
            <AlertCircle size={18} />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg flex items-center gap-2 text-success">
            <CheckCircle size={18} />
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Back button */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-text-2 hover:text-text-1 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar ao Dashboard
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <MessageCircle size={24} className="text-accent-1" />
            <div>
              <h1 className="text-2xl font-bold text-text-1">Gestao de Acesso WhatsApp</h1>
              <p className="text-text-3 text-sm">Controle quem pode usar cada conta WhatsApp</p>
            </div>
          </div>
        </div>

        {/* WhatsApp Account Selector - Dropdown with details */}
        <div className="bg-bg-2 border border-bg-3 rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-text-2 mb-2">Selecionar Conta WhatsApp</label>

          <div className="relative" ref={dropdownRef}>
            {/* Selected Account Display */}
            <button
              onClick={() => setShowWabaDropdown(!showWabaDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 bg-bg-1 border border-bg-3 rounded-lg hover:border-accent-1 transition-colors"
            >
              {selectedWaba ? (
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex items-center gap-2 text-accent-1">
                    <MessageCircle size={20} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-text-1">
                      {selectedWaba.businessName || 'Sem nome'}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-text-3">
                      <span className="flex items-center gap-1">
                        <Phone size={12} />
                        {selectedWaba.displayPhoneNumber}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getQualityBadge(selectedWaba.qualityRating).color}`}>
                        {getQualityBadge(selectedWaba.qualityRating).label}
                      </span>
                      <span className="flex items-center gap-1 text-text-2">
                        <Gauge size={12} />
                        {getMessagingLimitLabel(selectedWaba.messagingLimitTier)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-text-3">Selecione uma conta...</span>
              )}
              <ChevronDown size={20} className={`text-text-3 transition-transform ${showWabaDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Options */}
            {showWabaDropdown && (
              <div className="absolute z-10 w-full mt-2 bg-bg-1 border border-border-1 rounded-lg shadow-xl max-h-80 overflow-y-auto">
                {wabaAccounts.length === 0 ? (
                  <div className="p-4 text-center text-text-3">
                    Nenhuma conta disponivel
                  </div>
                ) : (
                  wabaAccounts.map((waba) => {
                    const quality = getQualityBadge(waba.qualityRating);
                    const isSelected = selectedWaba?.id === waba.id;

                    return (
                      <button
                        key={waba.id}
                        onClick={() => {
                          setSelectedWaba(waba);
                          setShowWabaDropdown(false);
                        }}
                        className={`w-full px-4 py-3 flex items-center gap-4 hover:bg-bg-2 transition-colors border-b border-bg-3 last:border-b-0 ${
                          isSelected ? 'bg-accent-1/10' : 'bg-bg-1'
                        }`}
                      >
                        <div className={`flex items-center gap-2 ${isSelected ? 'text-accent-1' : 'text-text-3'}`}>
                          <MessageCircle size={20} />
                        </div>
                        <div className="flex-1 text-left">
                          <div className={`font-medium ${isSelected ? 'text-accent-1' : 'text-text-1'}`}>
                            {waba.businessName || 'Sem nome'}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-text-3 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Phone size={12} />
                              {waba.displayPhoneNumber}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${quality.color}`}>
                            {quality.label}
                          </span>
                          <span className="text-xs text-text-3 flex items-center gap-1">
                            <Gauge size={10} />
                            {getMessagingLimitLabel(waba.messagingLimitTier)}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {selectedWaba && (
          <>
            {/* Controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-text-2">
                  <input
                    type="checkbox"
                    checked={showIncludeInactive}
                    onChange={(e) => setShowIncludeInactive(e.target.checked)}
                    className="rounded border-bg-3 bg-bg-1 text-accent-1 focus:ring-accent-1"
                  />
                  Mostrar inativos
                </label>
              </div>

              <button
                onClick={() => setShowGrantModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-lg transition-colors"
              >
                <Plus size={18} />
                Conceder Acesso
              </button>
            </div>

            {/* Access List */}
            <div className="bg-bg-2 border border-bg-3 rounded-lg overflow-hidden">
              {loading ? (
                <LoadingSpinner size="lg" />
              ) : accessList.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-text-3 mb-3" />
                  <p className="text-text-2">Nenhum usuario tem acesso a esta conta WhatsApp</p>
                  <p className="text-text-3 text-sm">Clique em "Conceder Acesso" para adicionar</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-bg-3">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                        Usuario
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                        Limite Diario
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                        Uso (24h)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-2 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-text-2 uppercase">
                        Acoes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bg-3">
                    {accessList.map((access) => {
                      const usagePercent =
                        access.dailyLimit > 0
                          ? (access.usedIn24h / access.dailyLimit) * 100
                          : 0;
                      const isUnlimited = access.dailyLimit === 0;

                      return (
                        <tr
                          key={access.id}
                          className={`hover:bg-bg-3/50 ${!access.isActive ? 'opacity-50' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-text-1">
                              {access.user?.name}
                            </div>
                            <div className="text-xs text-text-3">{access.user?.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            {isUnlimited ? (
                              <span className="flex items-center gap-1 text-sm text-success">
                                <Infinity size={14} />
                                Ilimitado
                              </span>
                            ) : (
                              <span className="text-sm text-text-1">
                                {access.dailyLimit.toLocaleString()}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-text-1">
                                {access.usedIn24h.toLocaleString()}
                              </span>
                              {!isUnlimited && (
                                <div className="w-16 h-1.5 bg-bg-3 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${
                                      usagePercent > 90
                                        ? 'bg-error'
                                        : usagePercent > 70
                                        ? 'bg-warning'
                                        : 'bg-success'
                                    }`}
                                    style={{ width: `${Math.min(100, usagePercent)}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                access.isActive
                                  ? 'bg-success/20 text-success'
                                  : 'bg-error/20 text-error'
                              }`}
                            >
                              {access.isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleResetUsage(access)}
                                className="p-1.5 text-text-3 hover:text-accent-1 transition-colors"
                                title="Zerar uso"
                              >
                                <RotateCcw size={16} />
                              </button>
                              <button
                                onClick={() => openEditModal(access)}
                                className="p-1.5 text-text-3 hover:text-accent-1 transition-colors"
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleRevokeAccess(access)}
                                className="p-1.5 text-text-3 hover:text-error transition-colors"
                                title="Revogar"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Grant Access Modal */}
        {showGrantModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-bg-1 rounded-lg w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border-1">
                <h3 className="text-lg font-semibold text-text-1">Conceder Acesso WhatsApp</h3>
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="text-text-3 hover:text-text-2"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">Usuario</label>
                  <select
                    value={grantUserId}
                    onChange={(e) => setGrantUserId(e.target.value)}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  >
                    <option value="">Selecione um usuario</option>
                    {users
                      .filter(
                        (u) =>
                          u.role !== 'super_admin' &&
                          u.role !== 'admin' &&
                          !accessList.some((a) => a.userId === u.id)
                      )
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Limite Diario (0 = ilimitado)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={grantDailyLimit}
                    onChange={(e) => setGrantDailyLimit(e.target.value)}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Notas (opcional)
                  </label>
                  <textarea
                    value={grantNotes}
                    onChange={(e) => setGrantNotes(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                    placeholder="Observacoes sobre este acesso..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-border-1">
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="px-4 py-2 bg-bg-3 text-text-2 hover:bg-bg-3/80 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGrantAccess}
                  disabled={saving || !grantUserId}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving && <RefreshCw size={16} className="animate-spin" />}
                  Conceder
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Access Modal */}
        {showEditModal && selectedAccess && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-bg-1 rounded-lg w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between p-4 border-b border-border-1">
                <h3 className="text-lg font-semibold text-text-1">Editar Acesso</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-text-3 hover:text-text-2"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="bg-bg-2 border border-border-1 rounded-lg p-3">
                  <p className="text-sm text-text-3">Usuario</p>
                  <p className="text-text-1 font-medium">{selectedAccess.user?.name}</p>
                  <p className="text-xs text-text-3">{selectedAccess.user?.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Limite Diario (0 = ilimitado)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editDailyLimit}
                    onChange={(e) => setEditDailyLimit(e.target.value)}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-text-2">
                    <input
                      type="checkbox"
                      checked={editIsActive}
                      onChange={(e) => setEditIsActive(e.target.checked)}
                      className="rounded border-border-1 bg-bg-1 text-accent-1 focus:ring-accent-1"
                    />
                    Acesso Ativo
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1">
                    Notas (opcional)
                  </label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 bg-bg-1 border border-border-1 rounded-lg text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-border-1">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-bg-3 text-text-2 hover:bg-bg-3/80 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdateAccess}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-1/90 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {saving && <RefreshCw size={16} className="animate-spin" />}
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog {...dialogProps} />
      </main>
    </div>
  );
}
